import React, { useEffect, useMemo, useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import { obtenerPorCobrar, registrarAbono, obtenerAbonosDeCxc } from '../services/porCobrarService';
import { eliminarVenta } from '../services/balanceService';
import { registrarComisionBancaria } from '../services/gastosService';
import { calcularComision, COMISION_POS_PCT } from '../utils/comisiones';
import { obtenerComisionPct } from '../services/negociosService';
import { fechaLocal } from '../utils/fecha';
import { obtenerCuentas, iconoTipo } from '../services/cuentasService';
import { mensajeAmigable } from '../utils/errores';
import { numeroSeguro } from '../utils/validacion';
import { useToast } from '../context/ToastContext';
import CampoNumero from './CampoNumero';
import EditarVentaCreditoModal from './EditarVentaCreditoModal';
import EditarAbonoModal from './EditarAbonoModal';

const fmt = (n) => `${simboloMoneda()} ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fecha = (d) => new Date(d + 'T00:00:00').toLocaleDateString('es-HN', { day: '2-digit', month: '2-digit', year: 'numeric' });

// Fiado / Por cobrar: quién debe, cuánto, y registrar abonos (pagos).
export default function PorCobrarView({ onVolver, onCambio, productos = [] }) {
  const { mostrarToast } = useToast();
  const [lista, setLista] = useState(null);
  const [cuentas, setCuentas] = useState([]);
  const [error, setError] = useState(null);
  const [abonando, setAbonando] = useState(null); // id de la cxc con form abierto
  const [montoAbono, setMontoAbono] = useState('');
  const [cuentaAbono, setCuentaAbono] = useState('');
  const [comisionAbono, setComisionAbono] = useState(false); // cobrado con POS/Link Ficohsa (−4%)
  const [comisionPct, setComisionPct] = useState(COMISION_POS_PCT); // % configurable por negocio
  const [guardando, setGuardando] = useState(false);
  const [editarVentaCxc, setEditarVentaCxc] = useState(null); // cxc cuya venta se edita
  const [verPagos, setVerPagos] = useState(null); // cxc id con pagos abiertos
  const [pagos, setPagos] = useState([]); // abonos de la cxc abierta
  const [editarAbonoRef, setEditarAbonoRef] = useState(null); // abono a editar
  const [eliminando, setEliminando] = useState(null); // cxc con confirmación de eliminar venta
  const [borrando, setBorrando] = useState(false);

  const nombreCuenta = (id) => cuentas.find((c) => c.id === id)?.nombre || '—';
  const abrirPagos = async (c) => {
    if (verPagos === c.id) { setVerPagos(null); return; }
    try { setPagos(await obtenerAbonosDeCxc(c.id)); setVerPagos(c.id); }
    catch (e) { mostrarToast(mensajeAmigable(e), 'error'); }
  };
  const refrescar = () => { onCambio && onCambio(); cargar(); if (verPagos) obtenerAbonosDeCxc(verPagos).then(setPagos).catch(() => {}); };

  const cargar = () => {
    setLista(null); setError(null);
    obtenerPorCobrar({ soloPendientes: true })
      .then(setLista)
      .catch((e) => setError(mensajeAmigable(e)));
  };
  useEffect(() => {
    cargar();
    obtenerCuentas().then((cs) => { setCuentas(cs); if (cs.length) setCuentaAbono(cs[0].id); }).catch(() => {});
    obtenerComisionPct().then(setComisionPct).catch(() => {});
  }, []);

  const totalPorCobrar = useMemo(
    () => (lista || []).reduce((s, c) => s + (Number(c.monto_total) - Number(c.monto_pagado)), 0),
    [lista]
  );

  const abrirAbono = (c) => {
    setAbonando(c.id);
    setMontoAbono(String((Number(c.monto_total) - Number(c.monto_pagado)).toFixed(2)));
    setComisionAbono(false);
  };

  const confirmarAbono = async (c) => {
    const monto = numeroSeguro(montoAbono);
    if (!(monto > 0)) { mostrarToast('Escribe el monto del pago (solo números, mayor que 0)', 'error'); return; }
    // El abono nunca puede ser mayor a lo que la clienta debe.
    const pendiente = Math.round((Number(c.monto_total) - Number(c.monto_pagado)) * 100) / 100;
    if (monto > pendiente) {
      mostrarToast(`El pago no puede ser mayor a lo que debe (${fmt(pendiente)})`, 'error');
      return;
    }
    if (!cuentaAbono) { mostrarToast('Elige a qué cuenta entra el pago', 'error'); return; }
    setGuardando(true);
    try {
      await registrarAbono({ cxcId: c.id, monto, cuentaId: cuentaAbono });
      // Comisión bancaria automática si el abono se cobró con POS/Link.
      const comision = comisionAbono ? calcularComision(monto, comisionPct) : 0;
      if (comision > 0) {
        try {
          await registrarComisionBancaria({
            monto: comision, cuentaId: cuentaAbono, fecha: fechaLocal(),
            detalle: `POS / Link · abono ${c.clientes?.nombre || ''}`.trim(),
          });
        } catch (err) {
          mostrarToast('El pago se registró, pero no se pudo registrar la comisión. Agrégala a mano en 💸 Gastos.', 'error', 7000);
        }
      }
      mostrarToast('Pago registrado ✅', 'exito');
      setAbonando(null); setMontoAbono(''); setComisionAbono(false);
      onCambio && onCambio();
      cargar();
    } catch (e) {
      const msg = (e?.message || '').includes('MONTO_MAYOR_AL_SALDO') ? 'El monto es mayor a lo que debe.' : mensajeAmigable(e);
      mostrarToast(msg, 'error');
    } finally {
      setGuardando(false);
    }
  };

  // Elimina la venta a crédito completa (por su ticket): devuelve el producto
  // al inventario/catálogo y revierte los abonos. Reusa eliminar_venta.
  const confirmarEliminarVenta = async (c) => {
    setBorrando(true);
    try {
      await eliminarVenta(c.ticket_id);
      mostrarToast('Venta eliminada. El producto volvió al inventario ✅', 'exito');
      setEliminando(null);
      onCambio && onCambio();
      cargar();
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    } finally {
      setBorrando(false);
    }
  };

  const input = { width: '100%', padding: '9px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };

  return (
    <div className="k-page" style={{ maxWidth: '900px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <h2 className="k-page-title">🧾 Por cobrar (fiado)</h2>
        <button onClick={onVolver} className="k-btn-soft">← Volver</button>
      </div>

      <div style={{ backgroundColor: '#FEF3C7', borderRadius: '12px', padding: '16px', border: '1px solid #FCD34D', marginBottom: '20px' }}>
        <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#92400E', fontWeight: 600 }}>💰 Total por cobrar</p>
        <p style={{ margin: 0, fontSize: '26px', fontWeight: 'bold', color: '#B45309' }}>{fmt(totalPorCobrar)}</p>
      </div>

      {!lista && !error && <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>⏳ Cargando...</div>}
      {error && <div style={{ padding: '16px', backgroundColor: '#FEE2E2', borderRadius: '12px', border: '1px solid #FCA5A5', color: '#991B1B', fontWeight: 600 }}>⚠️ {error}</div>}

      {lista && lista.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>
          🎉 ¡Nadie te debe! No hay ventas a crédito pendientes.
        </div>
      )}

      {lista && lista.map((c) => {
        const saldo = Number(c.monto_total) - Number(c.monto_pagado);
        return (
          <div key={c.id} className="k-card" style={{ padding: '16px', marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
              <div>
                <p style={{ margin: 0, fontWeight: 'bold', color: '#111827', fontSize: '15px' }}>{c.clientes?.nombre || 'Cliente'}</p>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#6B7280' }}>{c.concepto || 'Venta a crédito'} · {fecha(c.fecha)}</p>
                {Number(c.monto_pagado) > 0 && (
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--k-verde)' }}>Abonado {fmt(c.monto_pagado)} de {fmt(c.monto_total)}</p>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>Debe</p>
                <p style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#B45309' }}>{fmt(saldo)}</p>
              </div>
            </div>

            {abonando === c.id ? (
              <div style={{ marginTop: '12px', backgroundColor: '#F0FDF4', borderRadius: '8px', padding: '12px', border: '1px solid #BBF7D0' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                  <div>
                    <label style={{ fontSize: '12px', color: '#065F46', fontWeight: 600 }}>Monto del pago</label>
                    <CampoNumero valor={montoAbono} onChange={setMontoAbono} deshabilitado={guardando}
                      min={0} max={saldo} prefijo="L"
                      maxMensaje={`No puede ser mayor a lo que debe (${fmt(saldo)})`}
                      estilo={input} />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: '#065F46', fontWeight: 600 }}>Entra a la cuenta</label>
                    <select value={cuentaAbono} disabled={guardando} onChange={(e) => setCuentaAbono(e.target.value)} style={input}>
                      {cuentas.length === 0 && <option value="">Crea una cuenta primero</option>}
                      {cuentas.map((ct) => <option key={ct.id} value={ct.id}>{iconoTipo(ct.tipo)} {ct.nombre}</option>)}
                    </select>
                  </div>
                </div>
                <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '8px', padding: '10px 12px', marginBottom: '8px', cursor: guardando ? 'default' : 'pointer' }}>
                  <input type="checkbox" checked={comisionAbono} disabled={guardando}
                    onChange={(e) => setComisionAbono(e.target.checked)} style={{ marginTop: '2px', width: '16px', height: '16px', flexShrink: 0 }} />
                  <span style={{ fontSize: '12px', color: '#1E40AF' }}>
                    <strong>💳 Cobrado con POS o Link de pago Ficohsa</strong> <span style={{ color: '#1D4ED8' }}>(−{comisionPct}% comisión)</span>
                    {comisionAbono && numeroSeguro(montoAbono) > 0 && (
                      <span style={{ display: 'block', marginTop: '3px', color: '#1D4ED8', fontWeight: 700 }}>
                        Se rebajará {fmt(calcularComision(parseFloat(montoAbono) || 0, comisionPct))} de tu cuenta como comisión bancaria.
                      </span>
                    )}
                  </span>
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => setAbonando(null)} disabled={guardando}
                    style={{ flex: '0 0 auto', padding: '9px 16px', background: 'white', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', color: '#6B7280', fontWeight: 600 }}>Cancelar</button>
                  <button onClick={() => confirmarAbono(c)} disabled={guardando}
                    style={{ flex: 1, padding: '9px', backgroundColor: guardando ? '#9CA3AF' : 'var(--k-verde)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
                    {guardando ? '⏳ Guardando...' : '💵 Registrar pago'}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button onClick={() => abrirAbono(c)}
                  style={{ padding: '9px 16px', backgroundColor: 'var(--k-verde)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
                  💵 Registrar pago
                </button>
                <button onClick={() => setEditarVentaCxc(c)}
                  style={{ padding: '9px 14px', background: 'white', color: '#B45309', border: '1px solid #FCD34D', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
                  ✏️ Editar venta
                </button>
                {Number(c.monto_pagado) > 0 && (
                  <button onClick={() => abrirPagos(c)}
                    style={{ padding: '9px 14px', background: 'white', color: '#7C3AED', border: '1px solid #DDD6FE', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
                    📜 Pagos
                  </button>
                )}
                <button onClick={() => setEliminando(c.id)}
                  style={{ padding: '9px 14px', background: 'white', color: '#B91C1C', border: '1px solid #FCA5A5', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
                  🗑️ Eliminar venta y devolver producto
                </button>
              </div>
            )}

            {eliminando === c.id && (
              <div style={{ marginTop: '10px', backgroundColor: '#FEF2F2', borderRadius: '8px', padding: '12px', border: '1px solid #FCA5A5' }}>
                <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#991B1B', fontWeight: 600 }}>
                  ¿Eliminar toda la venta? El producto (o productos) de esta venta vuelven al inventario y al catálogo, y se revierten los abonos. Esto no se puede deshacer.
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => setEliminando(null)} disabled={borrando}
                    style={{ flex: '0 0 auto', padding: '9px 16px', background: 'white', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', color: '#6B7280', fontWeight: 600 }}>Cancelar</button>
                  <button onClick={() => confirmarEliminarVenta(c)} disabled={borrando}
                    style={{ flex: 1, padding: '9px', backgroundColor: borrando ? '#9CA3AF' : '#DC2626', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
                    {borrando ? '⏳ Eliminando...' : '🗑️ Sí, eliminar y devolver producto'}
                  </button>
                </div>
              </div>
            )}

            {verPagos === c.id && (
              <div style={{ marginTop: '10px', backgroundColor: '#FAF5FF', borderRadius: '8px', padding: '10px', border: '1px solid #E9D5FF' }}>
                <p style={{ margin: '0 0 6px', fontSize: '12px', fontWeight: 700, color: '#7C3AED' }}>Pagos / abonos</p>
                {pagos.length === 0 && <p style={{ margin: 0, fontSize: '12px', color: '#9CA3AF' }}>Sin pagos.</p>}
                {pagos.map((a) => (
                  <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid #F3F4F6', fontSize: '13px' }}>
                    <span style={{ color: '#374151' }}>{fecha(a.fecha)} · {nombreCuenta(a.cuenta_id)}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <strong style={{ color: 'var(--k-verde)' }}>{fmt(a.monto)}</strong>
                      <button onClick={() => setEditarAbonoRef(a.id)} title="Editar pago" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px' }}>✏️</button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {editarVentaCxc && (
        <EditarVentaCreditoModal cxc={editarVentaCxc} productos={productos}
          onCerrar={() => setEditarVentaCxc(null)} onGuardado={refrescar} />
      )}
      {editarAbonoRef && (
        <EditarAbonoModal referencia={editarAbonoRef}
          onCerrar={() => setEditarAbonoRef(null)} onGuardado={refrescar} />
      )}
    </div>
  );
}
